// The handful of computer vision operations the scanner actually needs, written out rather than
// imported.
//
// OpenCV.js would do all of this and for a while it did. It cannot run here: its startup evaluates
// a string as JavaScript, and the deployment's content security policy forbids that. Allowing
// 'unsafe-eval' across every page of the product to get four operations is a bad trade — it is the
// single directive that turns a cross-site scripting bug from a nuisance into a takeover. Written
// out they come to a couple of hundred lines, run in the same few milliseconds, and take a ten
// megabyte download off the counter's first scan.

export const canvasOf = (source, width, height) => {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width));
  canvas.height = Math.max(1, Math.round(height));
  canvas.getContext('2d').drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvas;
};

export const pixelsOf = (canvas) => canvas.getContext('2d', { willReadFrequently: true })
  .getImageData(0, 0, canvas.width, canvas.height);

// Luminance as the eye weighs it, which is also what every one of these models was trained on.
export const grayscale = ({ data, width, height }) => {
  const out = new Uint8ClampedArray(width * height);
  for (let i = 0; i < out.length; i += 1) {
    out[i] = (data[i * 4] * 299 + data[i * 4 + 1] * 587 + data[i * 4 + 2] * 114) / 1000;
  }
  return { data: out, width, height };
};

// A separable box blur run twice, which is close enough to a Gaussian for everything here and is
// linear in the radius rather than quadratic.
export const blur = ({ data, width, height }, radius) => {
  let source = data;
  let target = new Uint8ClampedArray(width * height);
  for (let pass = 0; pass < 2; pass += 1) {
    for (let y = 0; y < height; y += 1) {
      let total = 0;
      for (let x = -radius; x <= radius; x += 1) total += source[y * width + Math.min(width - 1, Math.max(0, x))];
      for (let x = 0; x < width; x += 1) {
        target[y * width + x] = total / (radius * 2 + 1);
        total -= source[y * width + Math.min(width - 1, Math.max(0, x - radius))];
        total += source[y * width + Math.min(width - 1, Math.max(0, x + radius + 1))];
      }
    }
    const swap = source === data ? new Uint8ClampedArray(width * height) : source;
    for (let x = 0; x < width; x += 1) {
      let total = 0;
      for (let y = -radius; y <= radius; y += 1) total += target[Math.min(height - 1, Math.max(0, y)) * width + x];
      for (let y = 0; y < height; y += 1) {
        swap[y * width + x] = total / (radius * 2 + 1);
        total -= target[Math.min(height - 1, Math.max(0, y - radius)) * width + x];
        total += target[Math.min(height - 1, Math.max(0, y + radius + 1)) * width + x];
      }
    }
    source = swap;
    target = new Uint8ClampedArray(width * height);
  }
  return { data: source, width, height };
};

// Where the image changes fastest — the edge of a sheet of paper against a desk, and every stroke of
// ink on it. A 3×3 Sobel, magnitude approximated by the sum of the two directions, which is what
// every edge detector of this kind does before it thresholds anyway.
export const gradient = ({ data, width, height }) => {
  const out = new Uint8ClampedArray(width * height);
  for (let y = 1; y < height - 1; y += 1) {
    for (let x = 1; x < width - 1; x += 1) {
      const i = y * width + x;
      const tl = data[i - width - 1]; const tc = data[i - width]; const tr = data[i - width + 1];
      const ml = data[i - 1]; const mr = data[i + 1];
      const bl = data[i + width - 1]; const bc = data[i + width]; const br = data[i + width + 1];
      const horizontal = (tr + 2 * mr + br) - (tl + 2 * ml + bl);
      const vertical = (bl + 2 * bc + br) - (tl + 2 * tc + tr);
      out[i] = (Math.abs(horizontal) + Math.abs(vertical)) / 4;
    }
  }
  return { data: out, width, height };
};

/**
 * Every connected blob in a binary mask, as its bounding box.
 *
 * One pass joining each pixel to its neighbours above and to the left, a union-find to reconcile the
 * joins, and a second pass to accumulate the bounds. It is the classic two-pass labelling, and it is
 * what a contour tracer is used for here — the outlines themselves are never needed, only the box
 * around each one.
 */
export const components = (mask, width, height) => {
  const labels = new Int32Array(width * height);
  const parent = [0];
  const find = (start) => {
    let node = start;
    while (parent[node] !== node) { parent[node] = parent[parent[node]]; node = parent[node]; }
    return node;
  };
  const union = (a, b) => {
    const rootA = find(a); const rootB = find(b);
    if (rootA !== rootB) parent[rootB] = rootA;
  };
  let next = 1;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const i = y * width + x;
      if (!mask[i]) continue; // eslint-disable-line no-continue
      const neighbours = [];
      if (x > 0 && labels[i - 1]) neighbours.push(labels[i - 1]);
      if (y > 0 && labels[i - width]) neighbours.push(labels[i - width]);
      if (y > 0 && x > 0 && labels[i - width - 1]) neighbours.push(labels[i - width - 1]);
      if (y > 0 && x < width - 1 && labels[i - width + 1]) neighbours.push(labels[i - width + 1]);
      if (!neighbours.length) {
        parent[next] = next; labels[i] = next; next += 1;
      } else {
        const lowest = Math.min(...neighbours);
        labels[i] = lowest;
        neighbours.forEach((other) => union(lowest, other));
      }
    }
  }
  const found = new Map();
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const label = labels[y * width + x];
      if (!label) continue; // eslint-disable-line no-continue
      const root = find(label);
      const box = found.get(root);
      if (!box) {
        found.set(root, {
          left: x, top: y, right: x, bottom: y, count: 1,
        });
      } else {
        box.left = Math.min(box.left, x); box.right = Math.max(box.right, x);
        box.top = Math.min(box.top, y); box.bottom = Math.max(box.bottom, y);
        box.count += 1;
      }
    }
  }
  return [...found.values()];
};

// The eight unknowns of a projective transform, from four point pairs. Plain Gaussian elimination
// with partial pivoting on an 8×9 system — at this size nothing cleverer earns its keep.
const solve = (rows) => {
  const size = 8;
  for (let column = 0; column < size; column += 1) {
    let pivot = column;
    for (let row = column + 1; row < size; row += 1) {
      if (Math.abs(rows[row][column]) > Math.abs(rows[pivot][column])) pivot = row;
    }
    if (Math.abs(rows[pivot][column]) < 1e-9) return null;
    const swap = rows[column]; rows[column] = rows[pivot]; rows[pivot] = swap;
    for (let row = 0; row < size; row += 1) {
      if (row === column) continue; // eslint-disable-line no-continue
      const factor = rows[row][column] / rows[column][column];
      for (let k = column; k <= size; k += 1) rows[row][k] -= factor * rows[column][k];
    }
  }
  return rows.map((row, i) => row[size] / row[i]);
};

/**
 * The transform that takes the rectangle we want back to the quadrilateral we photographed.
 *
 * It is deliberately that way round. Filling the output means asking, for each pixel of the flat
 * page, where on the original photograph that pixel came from — and the answer has to be a position
 * between pixels, so it is sampled rather than copied.
 */
const homography = (corners, width, height) => {
  const to = [[0, 0], [width, 0], [width, height], [0, height]];
  const rows = [];
  for (let i = 0; i < 4; i += 1) {
    const [x, y] = to[i];
    const { x: u, y: v } = corners[i];
    rows.push([x, y, 1, 0, 0, 0, -x * u, -y * u, u]);
    rows.push([0, 0, 0, x, y, 1, -x * v, -y * v, v]);
  }
  const solved = solve(rows);
  return solved ? [...solved, 1] : null;
};

export const warp = (image, corners, width, height) => {
  const h = homography(corners, width, height);
  if (!h) return null;
  const out = new ImageData(width, height);
  const { data, width: sw, height: sh } = image;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const w = h[6] * x + h[7] * y + h[8];
      const sx = (h[0] * x + h[1] * y + h[2]) / w;
      const sy = (h[3] * x + h[4] * y + h[5]) / w;
      const x0 = Math.floor(sx); const y0 = Math.floor(sy);
      const fx = sx - x0; const fy = sy - y0;
      const at = (px, py) => (Math.min(sh - 1, Math.max(0, py)) * sw + Math.min(sw - 1, Math.max(0, px))) * 4;
      const a = at(x0, y0); const b = at(x0 + 1, y0); const c = at(x0, y0 + 1); const d = at(x0 + 1, y0 + 1);
      const target = (y * width + x) * 4;
      for (let channel = 0; channel < 3; channel += 1) {
        out.data[target + channel] = data[a + channel] * (1 - fx) * (1 - fy)
          + data[b + channel] * fx * (1 - fy)
          + data[c + channel] * (1 - fx) * fy
          + data[d + channel] * fx * fy;
      }
      out.data[target + 3] = 255;
    }
  }
  return out;
};

/**
 * Contrast stretched to the ink, then the edges sharpened.
 *
 * The stretch ignores the darkest and lightest two per cent so that one shadow in a corner or one
 * specular highlight off a laminated counter cannot decide the whole page's exposure. Deliberately
 * not a threshold: a figure written in blue ballpoint survives contrast and does not survive being
 * forced to black or white.
 */
export const enhance = (image) => {
  const gray = grayscale(image);
  const histogram = new Uint32Array(256);
  for (let i = 0; i < gray.data.length; i += 1) histogram[gray.data[i]] += 1;
  const clip = gray.data.length * 0.02;
  let low = 0; let high = 255; let seen = 0;
  for (let value = 0; value < 256; value += 1) { seen += histogram[value]; if (seen > clip) { low = value; break; } }
  seen = 0;
  for (let value = 255; value >= 0; value -= 1) { seen += histogram[value]; if (seen > clip) { high = value; break; } }
  const span = Math.max(1, high - low);
  const stretched = new Uint8ClampedArray(gray.data.length);
  for (let i = 0; i < gray.data.length; i += 1) stretched[i] = ((gray.data[i] - low) * 255) / span;
  const soft = blur({ data: stretched, width: image.width, height: image.height }, 2);
  const out = new ImageData(image.width, image.height);
  for (let i = 0; i < stretched.length; i += 1) {
    // Unsharp masking: the picture plus what the blur threw away.
    const value = Math.max(0, Math.min(255, stretched[i] * 1.6 - soft.data[i] * 0.6));
    out.data[i * 4] = value; out.data[i * 4 + 1] = value; out.data[i * 4 + 2] = value; out.data[i * 4 + 3] = 255;
  }
  return out;
};
